import { Search, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { displayCategoryName } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { GdtfDateRangeInput } from "~/components/gdtf-date-input";
import { Button, Checkbox, Input, Label, type MultiSelectOption, SearchableMultiSelect } from "~/components/ui";

import { useEntityModelContext } from "~/features/entities/hooks";
import type { EntityListFilters } from "~/features/entities/types";

interface EntitiesSearchFormProps {
  filters: EntityListFilters;
  onApply: (next: EntityListFilters) => void;
}

export function EntitiesSearchForm({ filters, onApply }: EntitiesSearchFormProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { entityCategories, references } = useEntityModelContext();

  const [idOrName, setIdOrName] = useState(filters.idOrName ?? "");
  const [categoryIds, setCategoryIds] = useState<string[]>(filters.categoryIds);
  const [referenceIds, setReferenceIds] = useState<string[]>(filters.referenceIds);
  const [gdtfFrom, setGdtfFrom] = useState(filters.gdtfFrom ?? "");
  const [gdtfTo, setGdtfTo] = useState(filters.gdtfTo ?? "");
  const [excludeDeactivated, setExcludeDeactivated] = useState(filters.excludeDeactivated);

  // Re-seed the draft inputs whenever the committed URL filters change from outside the form
  // (browser back/forward, a shared link, a "filter by this category" action). `filters` is a
  // stable useMemo reference in the container, so this resets only on an actual filter change —
  // never while the user is editing the draft. Render-time reset keeps it flash-free (no Effect).
  const prevFiltersRef = useRef(filters);
  if (prevFiltersRef.current !== filters) {
    prevFiltersRef.current = filters;
    setIdOrName(filters.idOrName ?? "");
    setCategoryIds(filters.categoryIds);
    setReferenceIds(filters.referenceIds);
    setGdtfFrom(filters.gdtfFrom ?? "");
    setGdtfTo(filters.gdtfTo ?? "");
    setExcludeDeactivated(filters.excludeDeactivated);
  }

  const categoryOptions = useMemo<MultiSelectOption[]>(
    () =>
      entityCategories.map((c) => ({
        id: c.id,
        label: displayCategoryName({ name: c.name, i18nLabel: c.i18nLabel as Record<string, unknown> }, locale),
      })),
    [entityCategories, locale],
  );

  const referenceOptions = useMemo<MultiSelectOption[]>(
    () => references.map((r) => ({ id: r.id, label: r.shortCode ? `${r.shortCode} — ${r.title}` : r.title })),
    [references],
  );

  const submit = () => {
    onApply({
      idOrName: idOrName.trim() || undefined,
      categoryIds,
      referenceIds,
      gdtfFrom: gdtfFrom.trim() || undefined,
      gdtfTo: gdtfTo.trim() || undefined,
      excludeDeactivated,
    });
  };

  const reset = () => {
    setIdOrName("");
    setCategoryIds([]);
    setReferenceIds([]);
    setGdtfFrom("");
    setGdtfTo("");
    setExcludeDeactivated(true);
    onApply({ categoryIds: [], referenceIds: [], excludeDeactivated: true });
  };

  return (
    <form
      className="flex flex-col gap-5 text-[13px]"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="entity-search-id" className="text-12-bold capitalize text-fg-2">
          {t("feature.entities.search.idOrName")}
        </Label>
        <Input
          id="entity-search-id"
          value={idOrName}
          onChange={(e) => setIdOrName(e.target.value)}
          placeholder={t("feature.entities.search.idOrName")}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.category.modelNames")}</Label>
        <SearchableMultiSelect
          placeholder={t("model.category.modelNames")}
          searchPlaceholder={t("ui.search")}
          options={categoryOptions}
          selectedIds={categoryIds}
          onChange={setCategoryIds}
          emptyText={t("feature.common.noData")}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.reference.modelNames")}</Label>
        <SearchableMultiSelect
          placeholder={t("model.reference.modelNames")}
          searchPlaceholder={t("ui.search")}
          options={referenceOptions}
          selectedIds={referenceIds}
          onChange={setReferenceIds}
          emptyText={t("feature.common.noData")}
        />
      </div>

      <GdtfDateRangeInput
        label={t("feature.entities.search.dateGdtf")}
        from={gdtfFrom}
        to={gdtfTo}
        onFromChange={setGdtfFrom}
        onToChange={setGdtfTo}
      />

      <label htmlFor="entity-exclude-deactivated" className="flex items-center gap-2 text-fg-1">
        <Checkbox
          id="entity-exclude-deactivated"
          checked={excludeDeactivated}
          onCheckedChange={(checked) => setExcludeDeactivated(checked === true)}
        />
        <span>{t("feature.common.excludeDeactivated")}</span>
      </label>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm">
          <Search />
          <span>{t("ui.search")}</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={reset}>
          <X />
          <span>{t("ui.reset")}</span>
        </Button>
      </div>
    </form>
  );
}
